import { formatMoneyInteger } from 'legacy/lut/components/widgets/public/partner360/helpers/formatMoneyInteger'

import { FIN_FRACTION_DIGITS, PLANNING_BY_FY_FQ, SOB_FRACTION_DIGITS } from '../../config'
import { TSType } from '../../models'
import { IFinSobCheckItem } from '../../models/IFinSobCheckItem'
import { IFinSobValidationError } from '../../models/IFinSobValidationError'
import { IGetFinSobValueProps } from '../getFinSobValue'

import { ICheckFinSobValidationGroup, ICheckFinSobValues } from './models'

export interface IAddFinSobError {
  isError: boolean
  isWarning: boolean
  isSoB: boolean
  props: ICheckFinSobValidationGroup
  validationItems: IFinSobCheckItem
  category: string
  categoryKey: string
  compareToKey: string
  periodFy: string
  periodFq: string
  pointDiff: number
  currentValue: number
  currentValueName: string
  currentValueType: string
  currentValuePeriod: string
  compareToValue: number
  compareToValueType: string
  compareToValuePeriod: string
  currentJustificationMessage: any
  currentValProps: IGetFinSobValueProps
  sType: TSType
  compareToValueProps: IGetFinSobValueProps
  isCompareToItself: boolean
  validationErrors: IFinSobValidationError[]
}

export const addFinSobError = ({
  isError,
  isWarning,
  isSoB,
  props,
  validationItems,
  category,
  categoryKey,
  compareToKey,
  periodFy,
  periodFq,
  pointDiff,
  currentValue,
  currentValueName,
  currentValueType,
  currentValuePeriod,
  compareToValue,
  compareToValueType,
  compareToValuePeriod,
  currentJustificationMessage,
  currentValProps,
  sType,
  compareToValueProps,
  isCompareToItself,
  validationErrors,
}: IAddFinSobError) => {
  const fractionDigits = isSoB ? SOB_FRACTION_DIGITS : FIN_FRACTION_DIGITS

  const commonFinSobCheckError: IFinSobValidationError = {
    inputKey: '',
    inputIndex: 0,
    groupIndex: props.groupIndex,
    key: `${validationItems.key}-error`,
    category,
    categoryKey,

    compareToKey:
      compareToKey ||
      getCompareToKey(
        periodFy,
        periodFq,
        compareToValueProps.fq,
        validationItems.current.type ?? 'targets',
        validationItems.compareTo.type ?? 'targets',
        validationItems.thresholds.error,
        validationItems.compareTo.value
      ),

    order: pointDiff,
    diff: formatMoneyInteger(pointDiff ?? 0, fractionDigits),
    threshold: (validationItems.thresholds.error ?? 0).toString(),
    currentValue: formatMoneyInteger(currentValue ?? 0, fractionDigits),
    currentValueName,
    currentValueType,
    currentValuePeriod,
    comparisonValue: formatMoneyInteger(compareToValue ?? 0, fractionDigits),
    comparisonValueName: `${compareToValueType} ${compareToValuePeriod}`,
    comparisonValueType: compareToValueType,
    comparisonValuePeriod: compareToValuePeriod,

    isJustificationRequired: true,
    justificationMessage: currentJustificationMessage,

    settingIndex: currentValProps.settingsIndex,
    valueType: currentValProps.type !== 'partnerCount' ? currentValProps.type : 'actuals',
    sType,
    year: currentValProps.fy,
    q: currentValProps.fq,

    isSob: currentValProps.isSoB,
    isUnits: currentValProps.isUnits,
    isChecked: false,

    compareToValueType:
      compareToValueProps.type !== 'partnerCount' ? compareToValueProps.type : 'actuals',
    compareToYear: compareToValueProps.fy,
    compareToQ: compareToValueProps.fq,
    isShortCompareToKeyOnly: isCompareToItself || !!compareToKey,
  }

  const inputKey = getInputKey({
    ...props,

    settingsIndex: currentValProps.settingsIndex,
    fy: currentValProps.fy,
    fq: currentValProps.fq,
    type: currentValProps.type,
    isSoB: currentValProps.isSoB,
    isUnits: currentValProps.isUnits,
    sType: currentValProps.sType,
  })

  if (isError) {
    const inputIndex = getInputIndex(inputKey, props.inputKeys)
    validationErrors.push({ ...commonFinSobCheckError, inputKey, inputIndex })
  } else if (isWarning) {
    const inputIndex = getInputIndex(inputKey, props.inputKeys)
    validationErrors.push({
      ...commonFinSobCheckError,

      inputKey,
      inputIndex,
      key: `${validationItems.key}-warning`,
      threshold: (validationItems.thresholds.warning ?? 0).toString(),
      isJustificationRequired: false,
    })
  }
}

const getInputKey = (props: ICheckFinSobValues): string => {
  let finSob = props.isSoB ? 'sob' : 'fin'
  if (props.isUnits) {
    finSob = 'units'
  }

  let sType = ''
  if (props.isUseContractuals) {
    sType = `-${props.sType ? props.sType : 'total'}`
  }

  return `cat${props.settingsIndex}-fy${props.fy % 100}-q${props.fq}-${props.type}-${finSob}${sType}`
}

const getInputIndex = (inputKey: string, inputKeys: string[]): number => {
  let index = inputKeys.indexOf(inputKey)
  if (index === -1) {
    index = inputKeys.push(inputKey) - 1
  }

  return index
}

/**
 * Translation keys
 */
const getCompareToKey = (
  periodFy: string,
  periodFq: string,
  fq: number,
  valueType: string,
  compareToType: string,
  thresholds: number,
  value?: 'fin' | 'sob' | 'units' | 'self'
): string => {
  const type = valueType === compareToType ? '' : `-${compareToType}`
  const fqPart = fq === PLANNING_BY_FY_FQ ? '-fy' : `-fq`
  let compareToKey = `jbp-planning-check-${periodFy}-fy-${periodFq}${fqPart}${type}`

  if (value === 'self') {
    const selfCheckType = thresholds === -1 ? 'below-zero-value' : 'zero-value'
    compareToKey = `jbp-planning-check-self-${selfCheckType}`
  }

  return compareToKey
}
